<template>
  <div class="bernoulli-lift-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P + ½ρv² = Const$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>流速 (Airspeed)</label>
            <span class="val">{{ airSpeed }} <small>m/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>产生升力 (Lift Force)</label>
            <span class="val">{{ calculatedLift.toFixed(1) }} <small>kN</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-lift"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 背景流线 -->
          <g v-for="y in 12" :key="'line'+y">
             <path :d="calcStreamline(y*18 + 10)" 
                   fill="none" 
                   :stroke="y*18 + 10 < 120 ? '#38bdf8' : '#64748b'" 
                   :stroke-width="y*18 + 10 < 120 ? 1.5 : 1" 
                   :opacity="y*18 + 10 < 120 ? 0.6 : 0.3"
                   stroke-dasharray="10, 150">
                <animate attributeName="stroke-dashoffset" from="160" to="0" :dur="(2.5 - airSpeed/50) + 's'" repeatCount="indefinite" />
             </path>
          </g>

          <!-- 机翼剖面 -->
          <g transform="translate(140, 110)">
             <path d="M 0,10 C 30,-20 90,-20 120,5 C 130,15 100,20 0,15 Z" fill="#1e293b" stroke="#334155" stroke-width="2" />
             <path d="M 0,10 C 30,-20 90,-20 120,5" fill="none" stroke="#38bdf8" stroke-width="3" opacity="0.4" filter="url(#glow-lift)" />
             <text x="50" y="35" fill="#475569" font-size="10" font-weight="bold">AIRFOIL</text>
          </g>

          <!-- 压强标注 -->
          <g v-if="airSpeed > 10">
             <text x="200" y="70" text-anchor="middle" fill="#38bdf8" font-size="11" font-weight="bold">低压区 (Fast v, Low P)</text>
             <text x="200" y="160" text-anchor="middle" fill="#94a3b8" font-size="11">高压区 (Slow v, High P)</text>
          </g>

          <!-- 升力矢量箭头 -->
          <g v-if="airSpeed > 0" transform="translate(200, 100)">
             <line x1="0" y1="0" x2="0" :y2="-calculatedLift * 0.8" stroke="#fbbf24" stroke-width="5" marker-end="url(#arr-lift)" filter="url(#glow-lift)" />
             <text x="10" :y="-calculatedLift * 0.6" fill="#fbbf24" font-size="12" font-weight="900">LIFT</text>
          </g>

          <defs>
             <marker id="arr-lift" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" />
             </marker>
          </defs>
        </svg>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                 <span class="label">调节环境风速 (Wind Speed)</span>
                 <span class="val-tag">{{ airSpeed }} m/s</span>
              </div>
              <el-slider v-model="airSpeed" :min="0" :max="100" size="small"></el-slider>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>根据伯努利方程，流速越快的地方压强越小。由于机翼上表面弯曲，气流流经上表面的路程更长且速度更快，从而产生了向上的<strong>压力差</strong>。当风速增加一倍，升力通常会增加到原来的<strong>四倍</strong>（与v²成正比）。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BernoulliLiftViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      airSpeed: 45
    }
  },
  computed: {
    calculatedLift() {
      // Lift proportional to v^2
      return (this.airSpeed * this.airSpeed) * 0.01;
    }
  },
  methods: {
    calcStreamline(yBase) {
      let d = `M 0 ${yBase} `;
      for (let x = 10; x <= 400; x += 10) {
        let y = yBase;
        if (x > 100 && x < 300) {
           let dist = Math.abs(x - 200);
           let factor = (100 - dist) / 100;
           if (yBase < 120) {
              // Upper streamlines squeezed/upwards
              y -= 25 * factor * (1 - Math.abs(yBase - 110)/60);
           } else {
              // Lower streamlines slightly downwards
              y += 5 * factor * (1 - Math.abs(yBase - 130)/60);
           }
        }
        d += `L ${x} ${y.toFixed(1)} `;
      }
      return d;
    }
  }
}
</script>

<style scoped lang="scss">
.bernoulli-lift-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #fbbf24; }
    }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f8fafc; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; font-family: monospace; }

.discovery-card-compact {
  background: #eff6ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #dbeafe; display: flex; align-items: center; gap: 10px;
  i { color: #3b82f6; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #1e3a8a; line-height: 1.5; strong { color: #1e3a8a; } }
}
</style>
